import axios from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { Palette } from '../utils/constants'
import { formatSeriesNames } from '../utils/string'
import { BaseContext } from './'
import { CommonChartConfig } from './common'

const MARKET_STRUCTURE_CHARTS_PATH = '/api/charts/market-structure'

export namespace MarketStructure {
  export namespace MarketView {
    const CONTEXT_ID = 'equity:market_structure:market_view'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'short_stock' | 'trading_sock' | 'ratio_short_float'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        chartTitle:
          config.chart === 'short_stock'
            ? 'Short total'
            : config.chart === 'trading_sock'
            ? 'Volume negociado'
            : 'Short / Custódia',
        ...(config.chart === 'ratio_short_float'
          ? { convertToPercentages: [true] }
          : {}),
        tooltip: {
          pointFormatter: magnitudePointFormatter({
            unit: config.chart === 'ratio_short_float' ? '%' : '',
          }),
        },
      })(config, data)
  }

  export namespace Historical {
    const CONTEXT_ID = 'equity:market_structure:historical'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type MarketViewChartId =
      | 'historical'
      | 'share_historical'
      | 'rolling_historical'
      | 'rolling_share_historical'

    export interface ChartConfig extends CommonChartConfig {
      chart: MarketViewChartId
      context: typeof CONTEXT_ID
      corretoraIds: string[]
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const MARKET_VIEW_CHARTS_PATHS: Record<MarketViewChartId, string> = {
      historical: '/market-view/historical',
      share_historical: '/market-view/share-historical',
      rolling_historical: '/market-view/rolling-historical',
      rolling_share_historical: '/market-view/rolling-share-historical',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          MARKET_STRUCTURE_CHARTS_PATH + MARKET_VIEW_CHARTS_PATHS[config.chart],
        method: 'POST',
        data: { corretora_ids: config.corretoraIds },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isShareChart =
        config.chart === 'share_historical' ||
        config.chart === 'rolling_share_historical'
      return getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          convertToPercentages: Array(data.data.length).fill(isShareChart),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: isShareChart ? '%' : '',
            }),
          },
        },
        {
          ...(isShareChart
            ? { stacking: Array(data.data.length).fill('normal') }
            : {}),
          type: Array(data.data.length).fill(isShareChart ? 'area' : 'spline'),
        }
      )(config, data)
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config, {
          unorderedArrays: true,
        })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace KeyCharts {
    const CONTEXT_ID = 'equity:market_structure:key_charts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type KeyChartsChartId =
      | 'fundos'
      | 'acc_trade_fundos'
      | 'instituicao_financeira'
      | 'acc_trade_instituicao_financeira'
      | 'investidor_nao_residente'
      | 'acc_trade_investidor_nao_residente'
      | 'pessoa_fisica'
      | 'acc_trade_pessoa_fisica'
      | 'pessoa_juridica'
      | 'acc_trade_pessoa_juridica'

    export interface ChartConfig extends CommonChartConfig {
      chart: KeyChartsChartId
      context: typeof CONTEXT_ID
      startDate: string
      endDate: string
    }

    interface TopBottomPoint {
      ticker: string
      value: number
    }

    interface TopBottomData extends CompassAPIDataBase {
      data: { bottom: TopBottomPoint[]; top: TopBottomPoint[] }
    }

    export type ChartData = TopBottomData | CompassAPIMultiSeriesChartData

    const KEY_CHARTS_PATHS: Record<KeyChartsChartId, string> = {
      fundos: '/key-charts/fundos',
      acc_trade_fundos: '/key-charts/acc-trade-fundos',
      instituicao_financeira: '/key-charts/instituicao-financeira',
      acc_trade_instituicao_financeira:
        '/key-charts/acc-trade-instituicao-financeira',
      investidor_nao_residente: '/key-charts/investidor-nao-residente',
      acc_trade_investidor_nao_residente:
        '/key-charts/acc-trade-investidor-nao-residente',
      pessoa_fisica: '/key-charts/pessoa-fisica',
      acc_trade_pessoa_fisica: '/key-charts/acc-trade-pessoa-fisica',
      pessoa_juridica: '/key-charts/pessoa-juridica',
      acc_trade_pessoa_juridica: '/key-charts/acc-trade-pessoa-juridica',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: MARKET_STRUCTURE_CHARTS_PATH + KEY_CHARTS_PATHS[config.chart],
        method: 'POST',
        data: { start_date: config.startDate, end_date: config.endDate },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'fundos':
          case 'instituicao_financeira':
          case 'investidor_nao_residente':
          case 'pessoa_fisica':
          case 'pessoa_juridica':
            return getTopBottomChartProps(config, data as TopBottomData)
          default:
            return getLineChartProps(
              config,
              data as CompassAPIMultiSeriesChartData
            )
        }
      },
    }

    const getTopBottomChartProps = (
      config: ChartConfig,
      data: TopBottomData
    ) => {
      const series = [
        {
          data: data.data
            ? [
                ...data.data.top,
                { ticker: '', value: 0 },
                ...data.data.bottom,
              ].map((column, idx) => ({
                name: column.ticker,
                y: column.value / 1000000,
                color:
                  column.value >= 0
                    ? '#30ba09' // success.main
                    : '#f4643d', // error.light
                ...(idx === data.data.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{point.y:.2f} mi' },
          color: Palette.green,
          showInLegend: false,
          yAxis: 'primary',
        },
      ]
      const creditsConfig = getDataSources(data.data_sources)

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: 'mi',
      }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data && data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data && data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [
        {
          id: 'primary',
          title: { text: '' },
          labels: { format: '{value} mi' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { type: 'bar' },
            tooltip,
            stockTools: { gui: { enabled: false } },
            xAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    const getLineChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          tooltip: { pointFormatter: magnitudePointFormatter() },
        },
        {
          type: Array(data.data.length).fill('spline'),
          visible: Array(data.data.length)
            .fill(false)
            .map((_, idx) => idx < 5),
        }
      )(config, data)
  }

  export namespace SecurityView {
    const CONTEXT_ID = 'equity:market_structure:security_view'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type SecurityViewChartId =
      | 'custody_stock_by_type'
      | 'derivative_custody_stock_by_type'
      | 'short_percentage'
      | 'short_tomador'
      | 'short_doador'
      | 'short_spread'
      | 'trading_percentage'
      | 'trading_comprador'
      | 'trading_vendedor'
      | 'trading_spread'

    export interface ChartConfig extends CommonChartConfig {
      chart: SecurityViewChartId
      context: typeof CONTEXT_ID
      ticker: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const SECURITY_VIEW_CHARTS_PATHS: Record<SecurityViewChartId, string> = {
      custody_stock_by_type: '/security-view/custody-stock-by-type',
      derivative_custody_stock_by_type:
        '/security-view/derivative-custody-stock-by-type',
      short_percentage: '/security-view/short-percentage-by-bbg',
      short_tomador: '/security-view/short-tomador',
      short_doador: '/security-view/short-doador',
      short_spread: '/security-view/short-spread',
      trading_percentage: '/security-view/trading-percentage',
      trading_comprador: '/security-view/trading-comprador',
      trading_vendedor: '/security-view/trading-vendedor',
      trading_spread: '/security-view/trading-spread',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          MARKET_STRUCTURE_CHARTS_PATH +
          SECURITY_VIEW_CHARTS_PATHS[config.chart],
        method: 'GET',
        params: { ticker: config.ticker },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isStackedChart =
        config.chart === 'short_tomador' || config.chart === 'short_doador'

      const isStackedSumChart =
        config.chart === 'custody_stock_by_type' ||
        config.chart === 'trading_comprador' ||
        config.chart === 'trading_vendedor'
      return getMultipleLineChartProps(
        Context,
        {
          chartTitle: config.title,
          convertToPercentages: Array(data.data.length).fill(
            config.chart !== 'trading_percentage'
          ),
          tooltip: {
            pointFormatter: magnitudePointFormatter({
              unit: config.chart !== 'trading_percentage' ? '%' : '',
            }),
            xDateFormat: '%B %Y',
          },
          xAxis: { labels: { format: '{value:%Y}' } },
          ...(isStackedSumChart ? { y: { min: 0, max: 100 } } : {}),
        },
        {
          ...(isStackedChart || isStackedSumChart
            ? {
                type: Array(data.data.length).fill('area'),
                stacking: Array(data.data.length).fill('normal'),
              }
            : { type: Array(data.data.length).fill('spline') }),
          showInLegend: Array(data.data.length).fill(data.data?.length !== 1),
        }
      )(config, formatSeriesNames(data))
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }
  }
}
